import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LoadingIndicator } from '../common/components/loading-indicator';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { Flex } from '@cad-arc';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { FrPage } from '../fr-page-layout/fr-page';
import { FrPageBody } from '../fr-page-layout/fr-page-body';
import { FrPageHeader } from '../fr-page-layout/fr-page-header';
import { useDexFormsQuery } from '../data-exchange/forms/use-dex-forms-query';
import { FR_HEADER_HEIGHT } from '../fr-page-layout/constants';
import { DexResultsLeftPane } from '../data-exchange/results/dex-results-left-pane';
import { useDexResults } from '../data-exchange/results/use-dex-results';
import { DexResultsRightPane } from '../data-exchange/results/dex-results-right-pane';
import { SelectedOriAliasProvider } from '../data-exchange/forms/use-selected-ori-alias';
import { FrDexNewSearchSection } from './fr-dex-new-search-section';
import { DexNotificationsDismisser } from '../data-exchange/results/dex-notifications-dismisser';
import { useDexResultsKeyboardNavigationAndScroll } from '../data-exchange/results/use-dex-results-keyboard-navigation';
import { useIsLinkToAssignedEventCheckboxVisible } from '../data-exchange/forms/use-is-link-to-assigned-even-visible';
import { useSelectDexResults, useReadAllDexQueries } from '../data-exchange/results/use-select-dex-results';
import { ROOT_PATH } from '../router/constants';
import { useUnitAssignment } from '../unit-assignment/unit-assignment-context';
import { globalAttributes } from '../common/global-attributes';
import { ErrorBoundary } from '../monitoring/error-boundary';
import {
    useFederatedQueryIds,
    useFederatedSearchQueryForms,
    useFederatedSearchNotifications,
} from '../federated-search/federated-search-context';
import { useResetDexNextResultsStoreInitialFrFields } from '../data-exchange/forms/use-reset-dex-next-results-store-initial-fr-fields';
import { useCadLinkedEntitiesByQueryId } from '../data-exchange/results/use-cad-linked-entities-by-query-id';
import { FederatedSearchPreviewRow } from '../data-exchange/results/dex-next-results-preview-row';
import { FederatedQueriesFilters, FederatedSearchParsedResult } from 'mark43-federated-search/dist/lib/types';
import { getUniversalSearchLabel } from '../common/helpers/get-usx-labels';
import { useIsDexEnabled } from '~/data-exchange/use-dex-access-guard';
import { UpdateToEventWrapper } from '../data-exchange/results/dex-next-results-panel';
import { getAgencyEventFromFederatedSearchQueryLinks, getFederatedQueryLinks } from '../data-exchange/results/utils';

const FederatedSearchClient = lazy(() =>
    import('federated-search/FederatedSearchClient').then((module) => ({
        default: module.FederatedSearchClient,
    }))
);

export const FrDexPage = observer(function FrDexPage() {
    const navigate = useNavigate();
    const { isLoading } = useDexFormsQuery();
    const { dexResultsStore } = useDexResults();
    const { selectDexResult, selectDexResultsSummary } = useSelectDexResults(dexResultsStore);
    const leftPanelListContainerRef = useRef<HTMLDivElement | null>(null);
    const rightPanelScrollContainerRef = useRef<HTMLDivElement | null>(null);
    const features = useEnabledFeatures();
    const unitAssignment = useUnitAssignment();
    const {
        resetDexNextResultsStoreInitialFrFields,
    } = useResetDexNextResultsStoreInitialFrFields();
    const isLinkToAssignedEventCheckboxVisible = useIsLinkToAssignedEventCheckboxVisible();
    const { isDexEnabled, isLoading: isDexAccessLoading } = useIsDexEnabled();
    const isPolice =
        unitAssignment?.unitAgencyTypeGlobalAttrId === globalAttributes.agencyTypeGlobal.POLICE;

    const isDexVisible = isDexEnabled && isPolice;
    const isDexNextVisible = isDexVisible && features.dexNextEnabled;

    const { mutate: cleanUnreadUsxQueries } = useReadAllDexQueries();
    const federatedSearchNotifications = useFederatedSearchNotifications();

    // Clear unread notifications when the DEX page is viewed or when new notifications arrive
    useEffect(() => {
        if (features.dexNextEnabled && federatedSearchNotifications.unreadQueryCount > 0) {
            cleanUnreadUsxQueries();
        }
    }, [cleanUnreadUsxQueries, federatedSearchNotifications.unreadQueryCount, features.dexNextEnabled]);

    useEffect(
        function autoNavigateFromDex() {
            if (isDexAccessLoading) {
                return;
            }

            if (!isDexVisible && !isDexNextVisible) {
                navigate(ROOT_PATH);
            }
        },
        [isDexNextVisible, isDexVisible, navigate, isDexAccessLoading]
    );

    const onKeyboardSelectsResult = useCallback(
        ({ summaryId, resultId }: { summaryId?: number; resultId?: number }) => {
            if (summaryId) {
                selectDexResultsSummary(summaryId);
            } else if (resultId) {
                selectDexResult(resultId);
            }
        },
        [selectDexResult, selectDexResultsSummary]
    );

    const { keyDownHandler } = useDexResultsKeyboardNavigationAndScroll(
        leftPanelListContainerRef,
        rightPanelScrollContainerRef,
        dexResultsStore,
        onKeyboardSelectsResult
    );

    const { queryForms } = useFederatedSearchQueryForms();

    const dexNextForm = queryForms.find(
        (queryForm) =>
            queryForm.queryableEntityType === dexResultsStore.frDexNextPrefillValues?.entityType
    );

    const getDexPageContent = () => {
        switch (dexResultsStore.frDexPageViewMode) {
            case 'NEW_FORM':
                return (
                    <ErrorBoundary name="FR_DEX_NEW_SEARCH">
                        <SelectedOriAliasProvider>
                            <FrDexNewSearchSection />
                        </SelectedOriAliasProvider>
                    </ErrorBoundary>
                );
            case 'RESULTS':
                return (
                    <ErrorBoundary name="DEX_RESULTS_RIGHT_PANE">
                        <DexResultsRightPane
                            testId="FR_DEX_RESULTS_RIGHT_PANE"
                            scrollContainerRef={rightPanelScrollContainerRef}
                        />
                        <DexNotificationsDismisser />
                    </ErrorBoundary>
                );
        }
    };

    useEffect(() => {
        return () => {
            if (features.dexNextEnabled) {
                resetDexNextResultsStoreInitialFrFields();
            }
        };
    }, [resetDexNextResultsStoreInitialFrFields, features.dexNextEnabled]);

    const federatedQueryIds = useFederatedQueryIds();

    const filters: FederatedQueriesFilters = useMemo(() => {
        return {
            federatedQueryIds,
            initiatedBy: [],
        };
    }, [federatedQueryIds]);

    const {
        data: federatedSearchQueryLinkDtos,
        isLoading: isLinkedEntitiesLoading,
        isError: isLinkedEntitiesError,
    } = useCadLinkedEntitiesByQueryId(federatedQueryIds);

    const [selectedFederatedQueryId, setSelectedFederatedQueryId] = useState<string>('');

    const { agencyEventId } = useMemo(() => {
        const federatedQueryLinksForCurrentQueryId = getFederatedQueryLinks(
            federatedSearchQueryLinkDtos,
            selectedFederatedQueryId
        );
        return getAgencyEventFromFederatedSearchQueryLinks(federatedQueryLinksForCurrentQueryId);
    }, [selectedFederatedQueryId, federatedSearchQueryLinkDtos]);

    return (
        <FrPage>
            <FrPageHeader
                title={`${getUniversalSearchLabel(features.dexNextEnabled)}`}
                showMoreActionsMenu={features.dexNextEnabled}
            />
            <FrPageBody>
                <Flex
                    height={`calc(100vh - ${FR_HEADER_HEIGHT}px)`}
                    {...(dexResultsStore?.frDexPageViewMode === 'RESULTS'
                        ? { onKeyDown: keyDownHandler }
                        : {})}
                >
                    {features.dexNextEnabled && (
                        <Suspense fallback={<LoadingIndicator />}>
                            <FederatedSearchClient
                                federatedQueriesFilters={filters}
                                applicationScope="FIRST_RESPONDER"
                                defaultSelectedForm={dexNextForm}
                                defaultQueryFormValues={{
                                    ...(dexResultsStore.frDexNextPrefillValues?.prefillValues ||
                                        {}),
                                    LINK_CURRENT_ASSIGNED_EVENT: isLinkToAssignedEventCheckboxVisible,
                                }}
                                queryFormNodesOverrides={{
                                    CONTEXT_INFO_CARD: {
                                        hidden: !isLinkToAssignedEventCheckboxVisible,
                                    },
                                }}
                                componentsProps={{
                                    FederatedQueryResultDetails: {
                                        renderButtons: (props: { activeQueryResult?: { queryId?: string; id?: string }; parsedResultObject?: FederatedSearchParsedResult }) => {
                                            const queryId = props.activeQueryResult?.queryId;
                                            const queryResultId = props.activeQueryResult?.id;
                                            const federatedSearchParsedResult = props.parsedResultObject;
                                            return queryId ? (
                                                <UpdateToEventWrapper
                                                    queryId={queryId}
                                                    queryResultId={queryResultId}
                                                    linkedEventId={agencyEventId}
                                                    federatedSearchParsedResult={federatedSearchParsedResult}
                                                />
                                            ) : null;
                                        },
                                    },
                                    FederatedSearchResult: {
                                        onQuerySelect: (federatedQueryId: string) => {
                                            setSelectedFederatedQueryId(federatedQueryId);
                                        },
                                        renderPreviewSummary: ({
                                            previewSummary,
                                            federatedQuery,
                                            isWarning,
                                            isDanger,
                                        }) => (
                                            <FederatedSearchPreviewRow
                                                previewSummary={previewSummary}
                                                federatedQuery={federatedQuery}
                                                federatedSearchQueryLinkDtos={
                                                    federatedSearchQueryLinkDtos
                                                }
                                                isLinkedEntitiesLoading={isLinkedEntitiesLoading}
                                                isLinkedEntitiesError={isLinkedEntitiesError}
                                                isWarning={isWarning}
                                                isDanger={isDanger}
                                            />
                                        ),
                                    },
                                }}
                            />
                        </Suspense>
                    )}
                    {!features.dexNextEnabled && (
                        <>
                            {isLoading && <LoadingIndicator />}
                            {!isLoading && (
                                <DexResultsLeftPane
                                    testId="FR_DEX_RESULTS_LEFT_PANE"
                                    listContainerRef={leftPanelListContainerRef}
                                />
                            )}
                            {!isLoading && getDexPageContent()}
                        </>
                    )}
                </Flex>
            </FrPageBody>
        </FrPage>
    );
});
